import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon, 
  WalletIcon 
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getDashboardAnalyticsApi } from '../../api/dashboardApi';



const Reports = () => {
  const [stats, setStats] = useState({ totalGave: 0, totalGot: 0, netBalance: 0 });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await getDashboardAnalyticsApi();
      if (res.data.success) {
        setStats(res.data.data.stats);
        
        // Map trendData to Recharts format
        const formattedData = res.data.data.trendData.map(item => {
          const dateObj = new Date(item.date);
          const shortMonth = dateObj.toLocaleString('en-US', { month: 'short' });
          const day = dateObj.getDate();
          return {
            name: `${shortMonth} ${day}`,
            'You Gave': item.gave,
            'You Got': item.got
          };
        });
        
        setChartData(formattedData);
      }
    } catch (error) {
      console.error('Error fetching analytics for reports:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full pb-10 max-w-7xl mx-auto space-y-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Business Reports</h1>
          <p className="text-[15px] font-medium text-gray-500 dark:text-gray-400 mt-1">
            Analytics and insights of your business cash flow.
          </p>
        </div>
        <button className="bg-red-50 dark:bg-red-900/20 text-[#dc343b] dark:text-red-400 px-4 py-2 rounded-xl font-bold text-[14px] hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
          Download PDF
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-2xl text-red-500 dark:text-red-400">
              <ArrowTrendingUpIcon className="w-6 h-6" />
            </div>
            <h3 className="text-gray-500 dark:text-gray-400 font-bold text-[14px] uppercase tracking-wider">Total You Gave</h3>
          </div>
          <p className="text-3xl font-extrabold text-gray-900 dark:text-white">₹{stats.totalGave.toLocaleString()}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-2xl text-green-500 dark:text-green-400">
              <ArrowTrendingDownIcon className="w-6 h-6" />
            </div>
            <h3 className="text-gray-500 dark:text-gray-400 font-bold text-[14px] uppercase tracking-wider">Total You Got</h3>
          </div>
          <p className="text-3xl font-extrabold text-gray-900 dark:text-white">₹{stats.totalGot.toLocaleString()}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl text-blue-500 dark:text-blue-400">
              <WalletIcon className="w-6 h-6" />
            </div>
            <h3 className="text-gray-500 dark:text-gray-400 font-bold text-[14px] uppercase tracking-wider">Net Balance</h3>
          </div>
          <p className={`text-3xl font-extrabold ${stats.netBalance >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
            {stats.netBalance < 0 ? '-' : ''} ₹{Math.abs(stats.netBalance).toLocaleString()}
          </p>
          <p className="text-[12px] text-gray-400 dark:text-gray-500 font-medium mt-1">
            {stats.netBalance >= 0 ? 'You will receive' : 'You will give'}
          </p>
        </div>
      </div>

      {/* Chart Section */}
      <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] h-[400px] transition-colors">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Cash Flow (Last 6 Months)</h2>
          <select className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2 font-bold text-[13px] text-gray-700 dark:text-gray-300 outline-none focus:ring-2 focus:ring-[#dc343b]/20 transition-colors">
            <option>Last 6 Months</option>
            <option>This Year</option>
          </select>
        </div>
        
        <ResponsiveContainer width="100%" height="85%">
          <BarChart
            data={chartData.length > 0 ? chartData : [{ name: 'No Data', 'You Gave': 0, 'You Got': 0 }]}
            margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 13, fontWeight: 600}} dy={10} />
            <YAxis axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 13, fontWeight: 600}} />
            <Tooltip 
              cursor={{fill: '#f9fafb'}} 
              contentStyle={{borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'}}
            />
            <Legend wrapperStyle={{paddingTop: '20px'}} iconType="circle" />
            <Bar dataKey="You Gave" fill="#ef4444" radius={[6, 6, 0, 0]} barSize={24} />
            <Bar dataKey="You Got" fill="#22c55e" radius={[6, 6, 0, 0]} barSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Reports;
